import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, LogIn, LogOut, Calendar, User,
  CheckCircle2, ChevronLeft, ChevronRight, Sun, Moon,
  MapPin, Trash2, Plus, Monitor, Smartphone, Phone, AlertTriangle, Pencil
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import EditPresenzaDialog from "../../components/personale/EditPresenzaDialog";
import { cn } from "../../lib/utils";
import PresenzeMensile from "./PresenzeMensile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Presenze = () => {
  const { getAuthHeaders, localeCorrente, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSelezionata, setDataSelezionata] = useState(new Date().toISOString().split("T")[0]);
  const [dettaglioTurni, setDettaglioTurni] = useState({ fasce: [], data: "" });
  const [presenzaOggi, setPresenzaOggi] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [tabAttiva, setTabAttiva] = useState("giornaliero");
  // Conferma uscita con attività pendenti
  const [confermaUscita, setConfermaUscita] = useState(false);
  const [attivitaPendenti, setAttivitaPendenti] = useState([]);
  const [editingPresenza, setEditingPresenza] = useState(null); // { presenza, operatoreNome }
  const [riparando, setRiparando] = useState(false);

  const handleRiparaNotturne = async () => {
    setRiparando(true);
    try {
      const res = await axios.post(`${API}/personale/presenze/ripara-notturne`, {}, { headers: getAuthHeaders() });
      const n = res.data?.riaperte || 0;
      if (n > 0) {
        toast.success(`${n} turno/i riaperto/i: i dipendenti possono di nuovo timbrare e lavorare.`);
      } else {
        toast.info("Nessun turno da riaprire: tutto in regola.");
      }
      fetchData();
    } catch (e) {
      toast.error("Errore durante la riapertura dei turni");
    } finally {
      setRiparando(false);
    }
  };

  useEffect(() => {
    if (localeCorrente?.id) fetchData();
  }, [localeCorrente?.id, dataSelezionata]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    setLoading(true);
    try {
      if (isAdmin) {
        const turniRes = await axios.get(`${API}/personale/presenze/dettaglio-turni?locale_id=${localeCorrente.id}&data=${dataSelezionata}`, { headers: getAuthHeaders() });
        setDettaglioTurni(turniRes.data);
      } else {
        const res = await axios.get(
          `${API}/personale/presenze/oggi?locale_id=${localeCorrente.id}&utente_id=${user?.id}`,
          { headers: getAuthHeaders() }
        );
        setPresenzaOggi(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimbra = async (tipo) => {
    // Prima dell'uscita: controlla attività non completate
    if (tipo === "uscita") {
      setGeoLoading(true);
      try {
        const oggi = new Date().toISOString().split("T")[0];
        const res = await axios.get(
          `${API}/calendario-attivita?locale_id=${localeCorrente.id}&data=${oggi}`,
          { headers: getAuthHeaders() }
        );
        const dati = res.data || {};
        const pendenti = [];
        // Pulizie non completate
        (dati.tasks || []).filter(t => !t.completata).forEach(t =>
          pendenti.push({ tipo: "pulizia", nome: t.nome || t.descrizione || "Pulizia" })
        );
        // Attività gestione non completate
        (dati.gestione || []).filter(t => !t.completata).forEach(t =>
          pendenti.push({ tipo: "gestione", nome: t.nome || t.titolo || "Attività" })
        );
        // Temperature non registrate
        (dati.frigoriferi || []).filter(f => !f.completata).forEach(f =>
          pendenti.push({ tipo: "temperatura", nome: `Temperatura ${f.nome}` })
        );
        if (pendenti.length > 0) {
          setAttivitaPendenti(pendenti);
          setConfermaUscita(true);
          setGeoLoading(false);
          return;
        }
      } catch {
        // Se il check fallisce, procedi comunque con la timbratura
      }
    }
    await eseguiTimbra(tipo);
  };

  const eseguiTimbra = async (tipo) => {
    setGeoLoading(true);
    try {
      let lat = null, lng = null;

      if ("geolocation" in navigator) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 });
          });
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch (e) {
          // GPS not available
        }
      }

      const res = await axios.post(
        `${API}/personale/presenze/timbra?locale_id=${localeCorrente.id}&utente_id=${user?.id}`,
        { tipo, latitudine: lat, longitudine: lng },
        { headers: getAuthHeaders() }
      );

      const turnoLabel = res.data?.turno ? ` (${res.data.turno})` : "";
      toast.success(tipo === "entrata" ? `Entrata registrata${turnoLabel}!` : `Uscita registrata${turnoLabel}!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella timbratura");
    } finally {
      setGeoLoading(false);
    }
  };

  const navigaData = (giorni) => {
    const d = new Date(dataSelezionata);
    d.setDate(d.getDate() + giorni);
    setDataSelezionata(d.toISOString().split("T")[0]);
  };

  const getDataLabel = () => {
    const d = new Date(dataSelezionata);
    const oggi = new Date().toISOString().split("T")[0];
    if (dataSelezionata === oggi) return "Oggi";
    return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  };

  const getRepartoStyle = (reparto) => {
    const styles = {
      cucina: { bg: "bg-orange-100", text: "text-orange-700" },
      sala: { bg: "bg-blue-100", text: "text-blue-700" },
      laboratorio: { bg: "bg-purple-100", text: "text-purple-700" },
      tutti: { bg: "bg-gray-100", text: "text-gray-700" }
    };
    return styles[reparto] || styles.tutti;
  };

  const getDistanceBadge = (distanza) => {
    if (distanza === null || distanza === undefined) return <span className="text-xs text-muted-foreground">--</span>;
    if (distanza <= 50) return <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700"><MapPin className="h-3 w-3" /> {distanza}m</span>;
    return <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"><MapPin className="h-3 w-3" /> {distanza}m</span>;
  };

  const getStatoBadge = (op) => {
    if (op.stato_entrata === "assente" && !op.ora_entrata) return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Assente</span>;
    if (op.stato_entrata === "ritardo") return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Ritardo +{op.minuti_entrata}min</span>;
    if (op.ora_entrata && op.ora_uscita) return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Completo</span>;
    if (op.ora_entrata) return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">In corso</span>;
    return <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">--</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[200px]"><div className="text-muted-foreground">Caricamento...</div></div>;
  }

  // === VISTA OPERATORE ===
  if (!isAdmin) {
    const turni = presenzaOggi?.turni || [];
    const turnoAttivo = presenzaOggi?.turno_attivo;
    const haEntrataAperta = presenzaOggi?.ha_entrata_aperta;
    const tuttiCompletati = presenzaOggi?.tutti_completati;
    const hasTurni = turni.length > 0;

    // Trova il turno attivo per mostrare le info
    const turnoAttivoInfo = turni.find(t => t.nome === turnoAttivo);
    // Trova il prossimo turno non completato (se turno attivo non c'è)
    const prossimoTurno = !turnoAttivo ? turni.find(t => !t.completato && !t.ora_entrata) : null;
    // Trova il turno aperto (entrata senza uscita) per la deadline
    // PRIORITA': turno aperto NON scaduto ha la precedenza (per mostrare Timbra Uscita)
    const turniAperti = turni.filter(t => t.ora_entrata && !t.ora_uscita);
    const turnoApertoNonScaduto = turniAperti.find(t => !t.uscita_scaduta);
    const turnoAperto = turnoApertoNonScaduto || turniAperti[0] || null;
    const turnoApertoScaduto = turnoAperto?.uscita_scaduta || false;
    const deadlineUscitaCorrente = turnoAperto?.deadline_uscita || null;

    // Se il turno aperto è scaduto MA c'è un altro turno da timbrare (entrata),
    // l'operatore deve poter timbrare l'entrata del nuovo turno
    const altroTurnoDaTimbrare = turnoApertoScaduto && (
      (turnoAttivoInfo && turnoAttivoInfo.nome !== turnoAperto?.nome && !turnoAttivoInfo.ora_entrata) ||
      (!turnoAttivo && turni.some(t => !t.completato && !t.ora_entrata && t.nome !== turnoAperto?.nome))
    );
    const prossimoDaTimbrare = altroTurnoDaTimbrare
      ? (turnoAttivoInfo && !turnoAttivoInfo.ora_entrata ? turnoAttivoInfo : turni.find(t => !t.completato && !t.ora_entrata && t.nome !== turnoAperto?.nome))
      : null;

    return (
      <div className="page-container" data-testid="presenze-page">

        {/* Dialog conferma uscita con attività pendenti */}
        <Dialog open={confermaUscita} onOpenChange={setConfermaUscita}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Attività non completate
              </DialogTitle>
              <DialogDescription>
                Hai {attivitaPendenti.length} {attivitaPendenti.length === 1 ? "attività" : "attività"} ancora in sospeso per oggi:
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 max-h-48 overflow-y-auto my-1">
              {attivitaPendenti.map((a, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 text-sm">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                  <span className="text-foreground">{a.nome}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Sei sicuro di voler timbrare l'uscita senza completarle?</p>
            <DialogFooter className="flex gap-2 mt-2">
              <Button
                variant="outline" className="flex-1"
                onClick={() => setConfermaUscita(false)}
                data-testid="btn-torna-attivita"
              >
                Torna alle attività
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={() => { setConfermaUscita(false); eseguiTimbra("uscita"); }}
                data-testid="btn-conferma-uscita-comunque"
              >
                <LogOut className="h-4 w-4 mr-1.5" />Esci comunque
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div>
          <h1 className="page-title flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Timbra
          </h1>
          <p className="page-subtitle">Registra entrata e uscita</p>
        </div>

        <div className="bg-gradient-to-br from-primary/10 to-blue-50 rounded-3xl p-6 border border-primary/20">
          <div className="text-center mb-6">
            <p className="text-foreground/60 text-sm">
              Oggi, {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
            </p>
            <p className="text-4xl font-bold text-foreground mt-2">
              {new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </p>
            {turnoAttivo && (
              <div className="mt-2 inline-flex items-center gap-2 px-4 py-1.5 bg-primary/15 rounded-full" data-testid="turno-attivo-badge">
                <Sun className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-primary">{turnoAttivo} ({turnoAttivoInfo?.ora_inizio} - {turnoAttivoInfo?.ora_fine})</span>
              </div>
            )}
            {!turnoAttivo && prossimoTurno && (
              <p className="mt-2 text-sm text-foreground/60">
                Prossimo turno: <span className="font-semibold">{prossimoTurno.nome}</span> alle {prossimoTurno.ora_inizio}
              </p>
            )}
            {!turnoAttivo && !prossimoTurno && hasTurni && tuttiCompletati && (
              <p className="mt-2 text-sm text-green-600 font-medium">Tutti i turni completati</p>
            )}
          </div>

          {/* Pulsante timbratura */}
          <div className="space-y-3 mb-6">
            {tuttiCompletati ? (
              <div className="text-center py-4">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                <p className="text-foreground font-medium">Giornata completata!</p>
              </div>
            ) : haEntrataAperta && turnoApertoScaduto && altroTurnoDaTimbrare ? (
              /* Turno precedente scaduto MA c'è un nuovo turno: mostra Timbra Entrata */
              <div>
                <p className="text-xs text-center text-amber-600 mb-2">
                  Uscita {turnoAperto?.nome} scaduta — verrà registrata automaticamente
                </p>
                <Button onClick={() => handleTimbra("entrata")} disabled={geoLoading} className="w-full h-16 text-lg font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-testid="timbra-entrata-btn">
                  {geoLoading ? "Registrazione..." : <><LogIn className="h-6 w-6 mr-3" /> Timbra Entrata{prossimoDaTimbrare ? ` (${prossimoDaTimbrare.nome})` : ""}</>}
                </Button>
              </div>
            ) : haEntrataAperta ? (
              <>
                {turnoApertoScaduto ? (
                  <div className="text-center py-4">
                    <Clock className="h-12 w-12 mx-auto text-amber-500 mb-2" />
                    <p className="text-foreground font-medium">Finestra di uscita scaduta</p>
                    <p className="text-xs text-foreground/50 mt-1">L'uscita verrà registrata automaticamente</p>
                  </div>
                ) : (
                  <div>
                    <Button onClick={() => handleTimbra("uscita")} disabled={geoLoading} className="w-full h-16 text-lg font-bold bg-red-500 hover:bg-red-600 text-white rounded-2xl" data-testid="timbra-uscita-btn">
                      {geoLoading ? "Registrazione..." : <><LogOut className="h-6 w-6 mr-3" /> Timbra Uscita</>}
                    </Button>
                    {deadlineUscitaCorrente && (
                      <p className="text-xs text-center text-foreground/50 mt-1.5">
                        Puoi timbrare l'uscita fino alle {deadlineUscitaCorrente}
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <Button onClick={() => handleTimbra("entrata")} disabled={geoLoading} className="w-full h-16 text-lg font-bold bg-green-500 hover:bg-green-600 text-white rounded-2xl" data-testid="timbra-entrata-btn">
                {geoLoading ? "Registrazione..." : <><LogIn className="h-6 w-6 mr-3" /> Timbra Entrata</>}
              </Button>
            )}
          </div>

          {/* Riepilogo turni della giornata */}
          {hasTurni && (
            <div className="space-y-2" data-testid="riepilogo-turni">
              <p className="text-xs font-semibold text-foreground/50 uppercase tracking-wider mb-2">Riepilogo turni</p>
              {turni.map((t, idx) => (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-xl border ${t.completato ? 'bg-green-50 border-green-200' : t.ora_entrata ? 'bg-blue-50 border-blue-200' : 'bg-card border-border'}`}>
                  <div className="flex items-center gap-3">
                    {t.completato ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : t.ora_entrata ? (
                      <Clock className="h-5 w-5 text-blue-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-border" />
                    )}
                    <div>
                      <p className="font-semibold text-sm text-foreground">{t.nome}</p>
                      <p className="text-xs text-foreground/50">{t.ora_inizio} - {t.ora_fine}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {t.ora_entrata && (
                      <p className="text-xs font-mono">
                        <span className="text-green-600">{t.ora_entrata}</span>
                        {t.ora_uscita && <span className="text-red-600"> - {t.ora_uscita}</span>}
                      </p>
                    )}
                    {!t.ora_entrata && <p className="text-xs text-foreground/40">--:--</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Retrocompatibilità: nessun turno */}
          {!hasTurni && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card rounded-2xl p-4 text-center border border-border">
                <LogIn className="h-6 w-6 mx-auto text-green-500 mb-2" />
                <p className="text-xs text-foreground/50">Entrata</p>
                <p className="text-xl font-bold text-foreground">{presenzaOggi?.ora_entrata || "--:--"}</p>
              </div>
              <div className="bg-card rounded-2xl p-4 text-center border border-border">
                <LogOut className="h-6 w-6 mx-auto text-red-500 mb-2" />
                <p className="text-xs text-foreground/50">Uscita</p>
                <p className="text-xl font-bold text-foreground">{presenzaOggi?.ora_uscita || "--:--"}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // === VISTA ADMIN ===
  return (
    <div className="page-container" data-testid="presenze-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Clock className="h-6 w-6 text-primary" />
            Presenze
          </h1>
          <p className="page-subtitle">Dettaglio timbrature per turno e reparto</p>
        </div>
        <Button
          onClick={handleRiparaNotturne}
          disabled={riparando}
          variant="outline"
          className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 text-xs font-bold gap-1.5"
          data-testid="ripara-notturne-btn"
        >
          {riparando ? (
            <div className="w-4 h-4 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
          ) : (
            <><AlertTriangle className="h-3.5 w-3.5" /> Riapri turni chiusi per errore</>
          )}
        </Button>
      </div>

      {/* Tab Giornaliero / Mensile */}
      <div className="flex gap-1 p-1 bg-muted/60 rounded-xl" data-testid="presenze-tabs">
        <button
          onClick={() => setTabAttiva("giornaliero")}
          className={cn(
            "flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all active:scale-[0.98]",
            tabAttiva === "giornaliero"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
          data-testid="tab-giornaliero"
        >
          Giornaliero
        </button>
        <button
          onClick={() => setTabAttiva("mensile")}
          className={cn(
            "flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all active:scale-[0.98]",
            tabAttiva === "mensile"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
          data-testid="tab-mensile"
        >
          Mensile
        </button>
      </div>

      {tabAttiva === "mensile" ? (
        <PresenzeMensile localeCorrente={localeCorrente} getAuthHeaders={getAuthHeaders} />
      ) : (
      <>
      {/* Selettore Data */}
      <div className="flex items-center justify-between bg-card rounded-2xl p-4 border border-border">
        <Button variant="ghost" size="icon" onClick={() => navigaData(-1)}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex flex-col items-center gap-1">
          <p className="text-foreground font-bold capitalize">{getDataLabel()}</p>
          <DateInput value={dataSelezionata} onChange={(v) => setDataSelezionata(v)} />
        </div>
        <Button variant="ghost" size="icon" onClick={() => navigaData(1)}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Lista Turni */}
      {dettaglioTurni.fasce.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>Nessun turno assegnato per questa data</p>
        </div>
      ) : (
        <div className="space-y-6">
          {dettaglioTurni.fasce.map((fascia, fasciaIdx) => (
            <div key={fasciaIdx} className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-3">
                  {fascia.ora_inizio < "14:00" ? <Sun className="h-6 w-6 text-amber-500" /> : <Moon className="h-6 w-6 text-indigo-500" />}
                  <div>
                    <h2 className="text-lg font-bold text-foreground">{fascia.fascia_nome || `${fascia.ora_inizio} - ${fascia.ora_fine}`}</h2>
                    <p className="text-sm text-muted-foreground">{fascia.ora_inizio} - {fascia.ora_fine}</p>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-border">
                {fascia.reparti.map((reparto, repartoIdx) => {
                  const style = getRepartoStyle(reparto.reparto);
                  return (
                    <div key={repartoIdx} className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${style.bg} ${style.text}`}>{reparto.reparto_nome}</span>
                        <span className="text-sm text-muted-foreground">{reparto.operatori.length} operatori</span>
                      </div>

                      <div className="overflow-x-auto -mx-4 px-4">
                        <table className="w-full min-w-[500px]">
                          <thead>
                            <tr className="text-xs uppercase text-muted-foreground">
                              <th className="py-2 px-2 text-left font-semibold">Operatore</th>
                              <th className="py-2 px-2 text-center font-semibold">Entrata</th>
                              <th className="py-2 px-2 text-center font-semibold">Uscita</th>
                              <th className="py-2 px-2 text-center font-semibold">GPS</th>
                              <th className="py-2 px-2 text-center font-semibold">Stato</th>
                              <th className="py-2 px-2 text-center font-semibold w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {reparto.operatori.map((op, opIdx) => (
                              <tr key={opIdx} className="border-t border-border/50 hover:bg-muted/30">
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${style.bg}`}>
                                      <User className={`h-4 w-4 ${style.text}`} />
                                    </div>
                                    <span className="font-medium text-foreground text-sm">{op.utente_nome}</span>
                                  </div>
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`font-mono text-sm font-medium ${op.ora_entrata ? "text-green-600" : "text-muted-foreground"}`}>
                                    {op.ora_entrata || "--:--"}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <span className={`font-mono text-sm font-medium ${op.ora_uscita ? "text-green-600" : "text-muted-foreground"}`}>
                                    {op.ora_uscita || "--:--"}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-center">
                                  {getDistanceBadge(op.distanza_entrata_metri)}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  {getStatoBadge(op)}
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                                    onClick={() =>
                                      setEditingPresenza({
                                        presenza: {
                                          id: op.presenza_id || op.id,
                                          ora_entrata: op.ora_entrata || "",
                                          ora_uscita: op.ora_uscita || "",
                                          note: op.note || "",
                                          modifiche: op.modifiche || [],
                                        },
                                        operatoreNome: op.utente_nome,
                                      })
                                    }
                                    title="Modifica orari"
                                    data-testid={`edit-presenza-btn-${op.utente_id || opIdx}`}
                                    disabled={!(op.presenza_id || op.id)}
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Dialog modifica presenza (admin) */}
      <EditPresenzaDialog
        open={!!editingPresenza}
        presenza={editingPresenza?.presenza}
        operatoreNome={editingPresenza?.operatoreNome}
        data={dataSelezionata}
        getAuthHeaders={getAuthHeaders}
        onClose={() => setEditingPresenza(null)}
        onSaved={fetchData}
      />
    </div>
  );
};

export default Presenze;
